import { AdElementWidget, resolveWidgetUrl } from '@adflow/types';
import type { ElementAnimationStyle } from '@adflow/utils';
import { FC, useMemo } from 'react';

type Props = {
  id: string;
  name: string;
  data: AdElementWidget['data'];
  position: AdElementWidget['position'];
  size: AdElementWidget['size'];
  rotation?: AdElementWidget['rotation'];
  isPreview?: boolean;
  animationStyle?: ElementAnimationStyle;
  animationTargetId?: string;
};

/**
 * Draws a widget layer as an iframe. A widget whose url fails `resolveWidgetUrl` draws nothing
 * in a served ad; in the editor and preview (`isPreview`) it is drawn as a dashed outline, so
 * the layer can still be seen and selected before its link is set.
 * In the editor and preview the iframe ignores the pointer, so a cross-origin page cannot
 * swallow the clicks that select and move the layer.
 */
const WidgetElement: FC<Props> = ({
  id,
  name,
  data,
  position,
  size,
  rotation,
  isPreview,
  animationStyle,
  animationTargetId,
}) => {
  const resolved = useMemo(() => resolveWidgetUrl(data), [data]);

  const transform =
    [animationStyle?.transform, rotation ? `rotate(${rotation}rad)` : undefined]
      .filter(Boolean)
      .join(' ') || undefined;

  if (!resolved.ok) {
    if (!isPreview) return null;

    return (
      <div
        id={id}
        data-animation-target-id={animationTargetId}
        data-widget-placeholder=""
        style={{
          position: 'absolute',
          left: position.x,
          top: position.y,
          width: size.width,
          height: size.height,
          zIndex: position.z,
          boxSizing: 'border-box',
          border: '2px dashed #8a94a6',
          background: 'rgba(138, 148, 166, 0.15)',
          pointerEvents: 'none',
          transform,
          opacity: animationStyle?.opacity,
        }}
      />
    );
  }

  return (
    <iframe
      id={id}
      data-animation-target-id={animationTargetId}
      src={resolved.url}
      title={name || 'Widget'}
      loading="eager"
      width={size.width}
      height={size.height}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: position.z,
        border: 0,
        display: 'block',
        pointerEvents: isPreview ? 'none' : undefined,
        transform,
        opacity: animationStyle?.opacity,
      }}
    />
  );
};

WidgetElement.displayName = 'WidgetElement';

export default WidgetElement;
